import { oc } from '@orpc/contract';
import { z } from 'zod';
import { commonErrors } from './common/errors.js';

/**
 * No `PERMISSION_DENIED` and no `OUT_OF_SCOPE`: a saved view belongs to one
 * user, is never shared and confers nothing, so there is no permission to hold
 * and no node to scope it against. A view belonging to somebody else is
 * `NOT_FOUND`, exactly as a row hidden by scope is elsewhere.
 */
const guarded = {
  UNAUTHENTICATED: commonErrors.UNAUTHENTICATED,
  TWO_FACTOR_REQUIRED: commonErrors.TWO_FACTOR_REQUIRED,
  NOT_FOUND: commonErrors.NOT_FOUND,
  CONFLICT: commonErrors.CONFLICT,
  VALIDATION_FAILED: commonErrors.VALIDATION_FAILED,
};

/**
 * The list screen this view belongs to, named in English and stable, because it
 * is a key the client sends and the unique constraint stores — renaming a
 * screen's label must not orphan everybody's saved views.
 */
const entitySchema = z
  .string()
  .trim()
  .min(2)
  .max(50)
  .regex(/^[a-z][a-zA-Z0-9]*$/);

const nameSchema = z.string().trim().min(1).max(80);

/**
 * The list screen's URL search params, which are strings on both sides of the
 * wire because that is what a query string is. Applying a view writes them back
 * through `useListUrlState`, so the URL stays the one source of list state.
 */
export const MAX_QUERY_ENTRIES = 30;

const querySchema = z
  .record(z.string(), z.string())
  .refine((value) => Object.keys(value).length <= MAX_QUERY_ENTRIES, {
    message: `A saved view carries at most ${MAX_QUERY_ENTRIES} query parameters`,
  });

const columnsSchema = z.array(z.string().trim().min(1).max(60)).max(60);

/** At most this many views per user per entity, enforced on save. */
export const MAX_VIEWS_PER_ENTITY = 50;

export const listViewSchema = z.object({
  id: z.uuid(),
  entity: z.string(),
  name: z.string(),
  query: z.record(z.string(), z.string()),
  hiddenColumns: z.array(z.string()),
  columnOrder: z.array(z.string()),
  isDefault: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type ListView = z.infer<typeof listViewSchema>;

export const listViewContract = {
  /**
   * Deliberately not paginated. The bound lives in the contract instead of in a
   * page cursor: a user may hold at most `MAX_VIEWS_PER_ENTITY` (50) views per
   * entity, refused on save, so the response is small by construction and the
   * screen can render the whole menu without a second round trip.
   */
  list: oc
    .route({ method: 'GET', path: '/list-views', summary: 'A user’s saved views for one list' })
    .errors(guarded)
    .input(z.object({ entity: entitySchema }))
    .output(z.object({ items: z.array(listViewSchema) })),

  save: oc
    .route({ method: 'POST', path: '/list-views' })
    .errors(guarded)
    .input(
      z.object({
        entity: entitySchema,
        name: nameSchema,
        query: querySchema.default({}),
        hiddenColumns: columnsSchema.default([]),
        columnOrder: columnsSchema.default([]),
        isDefault: z.boolean().default(false),
      }),
    )
    .output(z.object({ view: listViewSchema })),

  /**
   * Rename, re-save and set-default are one route, because they are one gesture
   * on the screen: the view menu's "update from the current list" writes the
   * query, the columns and the flag together, and sending only `name` renames.
   */
  update: oc
    .route({ method: 'PATCH', path: '/list-views/{id}' })
    .errors(guarded)
    .input(
      z.object({
        id: z.uuid(),
        name: nameSchema.optional(),
        query: querySchema.optional(),
        hiddenColumns: columnsSchema.optional(),
        columnOrder: columnsSchema.optional(),
        isDefault: z.boolean().optional(),
      }),
    )
    .output(z.object({ view: listViewSchema })),

  delete: oc
    .route({ method: 'DELETE', path: '/list-views/{id}' })
    .errors(guarded)
    .input(z.object({ id: z.uuid() }))
    .output(z.object({ id: z.uuid(), deleted: z.literal(true) })),
};
